import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    ArrowRight, Sun, Sparkles, Building2, Leaf, ShieldCheck, BadgeCheck, Zap,
    Wrench, ThermometerSun, Lightbulb, Wind, BarChart3, Euro, CheckCircle2, CloudSun,
} from "lucide-react";
import FinancingSimulator from "@/components/FinancingSimulator";
import CmsBackground from "@/components/CmsBackground";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysForMany } from "@/config/cmsSections";

const INTERESTS = [
    { value: "isolation", label: "Isolation thermique", icon: ThermometerSun },
    { value: "pompe_chaleur", label: "Pompe à chaleur", icon: Wind },
    { value: "led", label: "Éclairage LED", icon: Lightbulb },
    { value: "ventilation", label: "Ventilation VMC", icon: Wind },
    { value: "autoconsommation", label: "Autoconsommation solaire", icon: Sun },
    { value: "audit", label: "Audit énergétique", icon: BarChart3 },
];

const REGIONS = [
    { value: "nord", label: "Nord (Lille, Paris, Rennes)" },
    { value: "centre", label: "Centre (Lyon, Bordeaux, Nantes)" },
    { value: "sud", label: "Sud (Marseille, Toulouse, Montpellier)" },
];

const BUILDING_AGES = [
    { value: "<1980", label: "Avant 1980" },
    { value: "1980-2005", label: "Entre 1980 et 2005" },
    { value: ">2005", label: "Après 2005" },
];

export default function CEESolaire() {
    const c = useSiteContent(keysForMany("common", "cee-solaire"));
    // Solar simulator state
    const [rooftop, setRooftop] = useState([60]);
    const [annualKwh, setAnnualKwh] = useState([20000]);
    const [region, setRegion] = useState("centre");
    const [pricePerKwh, setPricePerKwh] = useState([0.22]);
    const [simResult, setSimResult] = useState(null);
    const [simLoading, setSimLoading] = useState(false);

    // Lead form state
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        company: "",
        siret: "",
        sector: "",
        employees: "",
        annual_kwh: "",
        building_age: "1980-2005",
        interests: [],
        rooftop_m2: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const runSim = async () => {
        setSimLoading(true);
        try {
            const { data } = await api.post("/solar/simulate", {
                rooftop_m2: Number(rooftop[0]),
                annual_kwh_consumption: Number(annualKwh[0]),
                region,
                current_price_kwh: Number(pricePerKwh[0]),
            });
            setSimResult(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSimLoading(false);
        }
    };

    const toggleInterest = (val) => {
        setForm((f) => {
            const set = new Set(f.interests);
            if (set.has(val)) set.delete(val); else set.add(val);
            return { ...f, interests: [...set] };
        });
    };

    const submitLead = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                employees: form.employees ? parseInt(form.employees, 10) : null,
                annual_kwh: form.annual_kwh ? Number(form.annual_kwh) : null,
                rooftop_m2: form.rooftop_m2 ? Number(form.rooftop_m2) : null,
            };
            await api.post("/cee-solar/leads", payload);
            setSubmitted(true);
            toast.success("Demande envoyée — un expert vous recontacte sous 24h");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const Euros = (n) => `${Math.round(n).toLocaleString("fr-FR")} €`;

    return (
        <div data-testid="cee-solaire-page">
            {/* HERO — plein écran sous le header flottant */}
            <section className="relative em-grain bg-secondary overflow-hidden -mt-20 md:-mt-20 min-h-screen flex items-center">
                <CmsBackground cmsKey="cee.hero.bg" />
                <div className="relative z-10 em-container pt-40 md:pt-44 pb-20 w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <p className="em-eyebrow flex items-center gap-2">
                                <Building2 className="h-3.5 w-3.5" />{c("cee.hero.eyebrow", "Pros · CEE · Autoconsommation")}
                            </p>
                            <h1
                                className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-primary leading-[1.05] mt-3"
                                dangerouslySetInnerHTML={{
                                    __html: c("cee.hero.title", "<span class=\"bg-accent text-primary px-3 rounded-md\">CEE</span> & Solaire B2B.<br />Le levier d'économie qu'on néglige."),
                                }}
                            />
                            <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-xl leading-relaxed">
                                {c("cee.hero.subtitle", "Identification des opérations éligibles, simulation des primes, accompagnement complet jusqu'à la mise en service.")}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 mt-7">
                                <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90 px-7 h-12" data-testid="hero-simulate-btn">
                                    <a href="#simulateur">
                                        <Sun className="h-4 w-4 mr-2" />
                                        {c("cee.hero.cta_primary", "Simuler mon potentiel solaire")}
                                    </a>
                                </Button>
                                <Button asChild size="lg" variant="outline" className="rounded-full px-7 h-12" data-testid="hero-cee-btn">
                                    <a href="#cee-form">
                                        {c("cee.hero.cta_secondary", "Demander un audit CEE")}
                                    </a>
                                </Button>
                            </div>
                            <div className="flex items-center gap-3 mt-7 text-sm text-muted-foreground">
                                <ShieldCheck className="h-4 w-4 text-primary" />
                                {c("cee.hero.trust", "CEE Délégataire conventionné · RGE QualiPV · Garantie décennale")}
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="grid grid-cols-2 gap-4">
                                <KPI icon={Sun} label={c("cee.hero.kpi1.label", "kWc installé")} value={c("cee.hero.kpi1.value", "10 kWc")} hint={c("cee.hero.kpi1.hint", "Toiture 80 m²")} />
                                <KPI icon={Euro} label={c("cee.hero.kpi2.label", "Économies / an")} value={c("cee.hero.kpi2.value", "1 500 €")} hint={c("cee.hero.kpi2.hint", "dès la 1ère année")} />
                                <KPI icon={Wrench} label={c("cee.hero.kpi3.label", "Aides CEE")} value={c("cee.hero.kpi3.value", "35-70%")} hint={c("cee.hero.kpi3.hint", "du montant des travaux")} />
                                <KPI icon={Leaf} label={c("cee.hero.kpi4.label", "CO₂ évité")} value={c("cee.hero.kpi4.value", "0,5 t/an")} hint={c("cee.hero.kpi4.hint", "par kWc installé")} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY */}
            <section className="em-section em-container">
                <p className="em-eyebrow">{c("cee.levers.eyebrow", "Les deux leviers d'économie pro")}</p>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary max-w-2xl mt-2">
                    {c("cee.levers.title", "CEE pour financer · Solaire pour produire")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                    <Card className="rounded-3xl border-border em-card-hover">
                        <CardContent className="p-8">
                            <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center">
                                <Wrench className="h-6 w-6 text-accent-foreground" />
                            </div>
                            <h3 className="font-display text-2xl font-bold text-primary mt-5">{c("cee.lever1.title", "Certificats d'Économies d'Énergie")}</h3>
                            <p className="text-muted-foreground mt-2 leading-relaxed">
                                {c("cee.lever1.body", "Les CEE financent jusqu'à 70% de vos travaux d'efficacité énergétique : isolation, pompe à chaleur, ventilation, éclairage LED, audit thermique… Energy Market vous accompagne du diagnostic à la prime versée.")}
                            </p>
                            <ul className="mt-5 space-y-2 text-sm">
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{c("cee.lever1.bullet1", "Diagnostic gratuit en 48h")}</li>
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{c("cee.lever1.bullet2", "Dossier administratif géré de A à Z")}</li>
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{c("cee.lever1.bullet3", "Versement des primes sous 90 jours")}</li>
                            </ul>
                        </CardContent>
                    </Card>
                    <Card className="rounded-3xl bg-primary text-primary-foreground border-0 em-card-hover">
                        <CardContent className="p-8">
                            <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center">
                                <Sun className="h-6 w-6 text-accent-foreground" />
                            </div>
                            <h3 className="font-display text-2xl font-bold mt-5">{c("cee.lever2.title", "Solaire en autoconsommation")}</h3>
                            <p className="text-primary-foreground/80 mt-2 leading-relaxed">
                                {c("cee.lever2.body", "Produisez votre propre électricité, consommez-la sur place, revendez le surplus. Centrale photovoltaïque sur toiture, ombrière de parking ou en sol.")}
                            </p>
                            <ul className="mt-5 space-y-2 text-sm">
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />{c("cee.lever2.bullet1", "Audit toiture et productible")}</li>
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />{c("cee.lever2.bullet2", "Installation clé en main RGE")}</li>
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />{c("cee.lever2.bullet3", "Suivi production et économies")}</li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* SOLAR SIMULATOR */}
            <section id="simulateur" className="relative em-section bg-secondary overflow-hidden">
                <CmsBackground cmsKey="cee.simulator.bg" />
                <div className="relative z-10 em-container">
                    <p className="em-eyebrow">{c("cee.simulator.eyebrow", "Simulateur solaire B2B")}</p>
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary max-w-2xl mt-2">
                        {c("cee.simulator.title", "Combien votre toiture peut-elle vous rapporter ?")}
                    </h2>
                    <p className="text-muted-foreground mt-3 max-w-2xl">{c("cee.simulator.body", "Indiquez votre surface exploitable et votre profil de consommation. Résultat instantané, basé sur les rendements moyens France 2025.")}</p>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
                        <Card className="rounded-3xl border-border bg-card">
                            <CardContent className="p-7 space-y-6">
                                <div>
                                    <div className="flex justify-between mb-3">
                                        <Label className="font-semibold">{c("cee.simulator.label_surface", "Surface de toiture exploitable (m²)")}</Label>
                                        <span className="font-mono text-primary font-bold" data-testid="sim-rooftop-value">{rooftop[0]} m²</span>
                                    </div>
                                    <Slider value={rooftop} onValueChange={setRooftop} min={20} max={1000} step={10} data-testid="sim-rooftop-slider" />
                                </div>
                                <div>
                                    <div className="flex justify-between mb-3">
                                        <Label className="font-semibold">{c("cee.simulator.label_kwh", "Consommation annuelle du site (kWh)")}</Label>
                                        <span className="font-mono text-primary font-bold" data-testid="sim-kwh-value">{annualKwh[0].toLocaleString("fr-FR")} kWh</span>
                                    </div>
                                    <Slider value={annualKwh} onValueChange={setAnnualKwh} min={5000} max={500000} step={1000} data-testid="sim-kwh-slider" />
                                </div>
                                <div>
                                    <div className="flex justify-between mb-3">
                                        <Label className="font-semibold">{c("cee.simulator.label_price", "Prix actuel du kWh (€)")}</Label>
                                        <span className="font-mono text-primary font-bold" data-testid="sim-price-value">{pricePerKwh[0].toFixed(2)} €</span>
                                    </div>
                                    <Slider value={pricePerKwh} onValueChange={setPricePerKwh} min={0.10} max={0.30} step={0.01} data-testid="sim-price-slider" />
                                </div>
                                <div>
                                    <Label className="em-eyebrow">{c("cee.simulator.label_region", "Région")}</Label>
                                    <Select value={region} onValueChange={setRegion}>
                                        <SelectTrigger className="rounded-xl mt-2" data-testid="sim-region"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {REGIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button onClick={runSim} disabled={simLoading} type="button" size="lg" className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="sim-submit">
                                    {simLoading ? "Calcul…" : c("cee.simulator.cta", "Calculer mon potentiel")}
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </CardContent>
                        </Card>

                        <div className="space-y-4">
                            {!simResult ? (
                                <div className="bg-card border-2 border-dashed border-border rounded-3xl p-10 text-center" data-testid="sim-empty">
                                    <CloudSun className="h-12 w-12 mx-auto text-primary/40" />
                                    <p className="font-display text-xl font-bold text-primary mt-4">{c("cee.simulator.empty_title", "Renseignez vos paramètres puis cliquez sur \"Calculer\".")}</p>
                                    <p className="text-muted-foreground mt-2 text-sm">{c("cee.simulator.empty_body", "Résultat instantané, basé sur les rendements moyens France 2025.")}</p>
                                </div>
                            ) : (
                                <>
                                    <Card className="rounded-3xl bg-primary text-primary-foreground border-0" data-testid="sim-result">
                                        <CardContent className="p-7 grid grid-cols-2 gap-5">
                                            <div>
                                                <p className="text-xs uppercase tracking-wider text-primary-foreground/70">Puissance installée</p>
                                                <p className="font-display text-3xl font-black text-accent mt-1" data-testid="sim-kwc">{simResult.install_kwc} kWc</p>
                                            </div>
                                            <div>
                                                <p className="text-xs uppercase tracking-wider text-primary-foreground/70">Production annuelle</p>
                                                <p className="font-display text-3xl font-black mt-1">{simResult.annual_production_kwh.toLocaleString("fr-FR")} <span className="text-sm text-primary-foreground/70">kWh</span></p>
                                            </div>
                                            <div>
                                                <p className="text-xs uppercase tracking-wider text-primary-foreground/70">Économies / an</p>
                                                <p className="font-display text-3xl font-black text-accent mt-1" data-testid="sim-savings">{Euros(simResult.annual_savings_eur)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs uppercase tracking-wider text-primary-foreground/70">Retour sur invest.</p>
                                                <p className="font-display text-3xl font-black mt-1">{simResult.payback_years} <span className="text-sm text-primary-foreground/70">ans</span></p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <div className="grid grid-cols-2 gap-3">
                                        <KPI icon={Euro} label="Coût installation" value={Euros(simResult.install_cost_eur)} hint="clé en main TTC" />
                                        <KPI icon={Leaf} label="CO₂ évité / an" value={`${simResult.co2_avoided_tons} t`} hint="vs grid France" />
                                        <KPI icon={Zap} label="Auto-consommé" value={`${simResult.self_consumption_kwh.toLocaleString("fr-FR")} kWh`} hint="≈ 35% production" />
                                        <KPI icon={Sun} label="Surplus revendu" value={`${simResult.surplus_kwh.toLocaleString("fr-FR")} kWh`} hint="≈ 0,10 €/kWh" />
                                    </div>
                                    <Button asChild className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-12" data-testid="sim-quote-cta">
                                        <a href="#cee-form">
                                            <ArrowRight className="h-4 w-4 mr-2" />
                                            {c("cee.simulator.result_cta", "Recevoir un devis détaillé")}
                                        </a>
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* FINANCING SIMULATOR (ROI + REGION) */}
            <section id="financement" className="relative em-section overflow-hidden">
                <CmsBackground cmsKey="cee.financing.bg" />
                <div className="relative z-10 em-container">
                    <p className="em-eyebrow">{c("cee.financing.eyebrow", "Plan de financement & ROI")}</p>
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary max-w-3xl mt-2">
                        {c("cee.financing.title", "Combien rapporte votre installation, vraiment ?")}
                    </h2>
                    <p className="text-muted-foreground mt-3 max-w-3xl">
                        {c("cee.financing.body", "Vous découvrez les modes de financement disponibles (apport, leasing, tiers-investissement) et la courbe de rentabilité prévisionnelle sur 25 ans.")}
                    </p>
                    <div className="mt-10">
                        <FinancingSimulator
                            defaultKwc={simResult ? Math.max(3, Math.round(simResult.install_kwc)) : 30}
                            defaultAnnualKwh={annualKwh[0]}
                        />
                    </div>
                </div>
            </section>

            {/* LEAD FORM */}
            <section id="cee-form" className="relative em-section overflow-hidden">
                <CmsBackground cmsKey="cee.form.bg" />
                <div className="relative z-10 em-container max-w-3xl">
                    <p className="em-eyebrow">{c("cee.form.eyebrow", "Formulaire pro")}</p>
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary mt-2">
                        {c("cee.form.title", "Demandez votre audit CEE & solaire.")}
                    </h2>
                    <p className="text-muted-foreground mt-3">{c("cee.form.body", "Indiquez vos coordonnées et le profil du site. Notre équipe revient sous 48h avec un plan d'action chiffré.")}</p>

                    {submitted ? (
                        <Card className="rounded-3xl mt-8 bg-primary text-primary-foreground border-0" data-testid="lead-success">
                            <CardContent className="p-10 text-center">
                                <CheckCircle2 className="h-16 w-16 mx-auto text-accent" />
                                <h3 className="font-display text-3xl font-black mt-4">{c("cee.form.success_title", "Demande reçue !")}</h3>
                                <p className="text-primary-foreground/80 mt-3">
                                    {c("cee.form.success_body", "Merci pour votre demande. Notre équipe revient vers vous sous 48h avec une proposition adaptée au profil de votre site.")}
                                </p>
                                <Button asChild className="rounded-full mt-6 bg-accent text-accent-foreground font-bold">
                                    <Link to="/">{c("cee.form.success_cta", "Retour à l'accueil")}</Link>
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="rounded-3xl border-border mt-8">
                            <CardContent className="p-7">
                                <form onSubmit={submitLead} className="space-y-5" data-testid="lead-form">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <Field label="Nom et prénom" required value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="lead-name" />
                                        <Field label="Société" required value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="lead-company" />
                                        <Field label="Email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="lead-email" />
                                        <Field label="Téléphone" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="lead-phone" />
                                        <Field label="SIRET (optionnel)" value={form.siret} onChange={(v) => setForm({ ...form, siret: v })} testid="lead-siret" />
                                        <Field label="Secteur d'activité" value={form.sector} onChange={(v) => setForm({ ...form, sector: v })} testid="lead-sector" />
                                        <Field label="Nb employés" type="number" value={form.employees} onChange={(v) => setForm({ ...form, employees: v })} testid="lead-employees" />
                                        <Field label="Conso annuelle (kWh)" type="number" value={form.annual_kwh} onChange={(v) => setForm({ ...form, annual_kwh: v })} testid="lead-annual-kwh" />
                                    </div>

                                    <div>
                                        <Label className="em-eyebrow">Âge du bâtiment</Label>
                                        <Tabs value={form.building_age} onValueChange={(v) => setForm({ ...form, building_age: v })} className="mt-2">
                                            <TabsList className="rounded-full">
                                                {BUILDING_AGES.map((b) => (
                                                    <TabsTrigger key={b.value} value={b.value} className="rounded-full" data-testid={`lead-age-${b.value}`}>
                                                        {b.label}
                                                    </TabsTrigger>
                                                ))}
                                            </TabsList>
                                        </Tabs>
                                    </div>

                                    <div>
                                        <Label className="em-eyebrow">Vos centres d'intérêt</Label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2" data-testid="lead-interests">
                                            {INTERESTS.map(({ value, label, icon: Icon }) => {
                                                const checked = form.interests.includes(value);
                                                return (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        onClick={() => toggleInterest(value)}
                                                        className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm transition-all ${
                                                            checked ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/30"
                                                        }`}
                                                        data-testid={`lead-interest-${value}`}
                                                    >
                                                        <Checkbox checked={checked} className="pointer-events-none" />
                                                        <Icon className="h-4 w-4" />
                                                        <span className="font-medium">{label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div>
                                        <Label className="em-eyebrow">Message (optionnel)</Label>
                                        <Textarea
                                            value={form.message}
                                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                                            rows={4}
                                            placeholder="Précisez vos besoins, projet, contraintes…"
                                            className="rounded-xl mt-2"
                                            data-testid="lead-message"
                                        />
                                    </div>

                                    <Button type="submit" disabled={submitting} size="lg" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-12" data-testid="lead-submit">
                                        {submitting ? "Envoi…" : c("cee.form.submit_label", "Recevoir mon audit gratuit")}
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </section>
        </div>
    );
}

function KPI({ icon: Icon, label, value, hint }) {
    return (
        <Card className="rounded-2xl border-border">
            <CardContent className="p-5">
                <Icon className="h-6 w-6 text-primary" />
                <p className="em-eyebrow mt-3">{label}</p>
                <p className="font-display text-2xl font-black text-primary mt-1">{value}</p>
                {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
            </CardContent>
        </Card>
    );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
    return (
        <div>
            <Label className="em-eyebrow">{label}{required && " *"}</Label>
            <Input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="rounded-xl mt-2"
                data-testid={testid}
            />
        </div>
    );
}
